// 🗓️ Coach de negocio · revisión de la semana (lunes a hoy), comparada con la semana
// pasada hasta el mismo día. Rendir cuentas: lo logrado, cuánto separar de lo vendido
// (Profit First: mercadería, tarjetas, lo tuyo y gastos) y qué ajustar.
import { calendarioPagos, deCada100 } from './finanzas';

const DIAS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];

export function lunesDe(fecha = new Date()) {
  const d = new Date(fecha.getFullYear(), fecha.getMonth(), fecha.getDate());
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return d;
}
export const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const tipoDe = (nombre) => { const t = String(nombre || '').split('·')[0].trim().toLowerCase(); return t ? t.charAt(0).toUpperCase() + t.slice(1) : ''; };

export function revisarSemana({ datos, resumen, meta, estrategias = [], recordatorios = [], hoy = new Date(), dinero = (n) => String(Math.round(n)) }) {
  const lunes = lunesDe(hoy);
  const hace7 = new Date(hoy.getTime() - 7 * 86400000);
  const lunesAntes = new Date(lunes.getTime() - 7 * 86400000);
  const ventas = datos.ventas || [];
  const deSemana = ventas.filter((v) => { const t = new Date(v.vendido_en); return t >= lunes && t <= hoy; });
  const deAntes = ventas.filter((v) => { const t = new Date(v.vendido_en); return t >= lunesAntes && t < hace7; });
  const suma = (l) => l.reduce((s, v) => s + Number(v.total || 0), 0);
  const tickets = (l) => new Set(l.map((v) => String(v.ticket_id || v.vendido_en))).size;
  const vendido = suma(deSemana); const antes = suma(deAntes); const nVentas = tickets(deSemana);

  // Por día (lunes a hoy) y el mejor día.
  const porDia = [];
  for (let d = new Date(lunes); d <= hoy; d = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1)) {
    const k = ymd(d);
    porDia.push({ fecha: k, nombre: DIAS[d.getDay()], dow: d.getDay(), total: suma(deSemana.filter((v) => ymd(new Date(v.vendido_en)) === k)) });
  }
  const mejorDia = porDia.reduce((a, b) => (b.total > (a?.total || 0) ? b : a), null);
  const diasVenta = meta?.dias_venta?.length ? meta.dias_venta : [1, 2, 3, 4, 5, 6];
  const diasSinVenta = porDia.filter((d) => d.fecha !== ymd(hoy) && diasVenta.includes(d.dow) && d.total <= 0).length;

  // Lo más vendido de la semana (por tipo de producto).
  const tipos = new Map();
  deSemana.forEach((v) => { const t = tipoDe(v.nombre_producto); if (!t) return; const x = tipos.get(t) || { nombre: t, unidades: 0, total: 0 }; x.unidades += Number(v.cantidad || 1); x.total += Number(v.total || 0); tipos.set(t, x); });
  const top = [...tipos.values()].sort((a, b) => b.total - a.total).slice(0, 3);

  // Meta de la semana: lo de un día de venta × días de venta de esta semana.
  const diasSemana = [0, 1, 2, 3, 4, 5, 6].filter((d) => diasVenta.includes(d)).length || 6;
  const metaSemana = resumen?.avance?.porDia ? resumen.avance.porDia * diasSemana : 0;

  // Cuánto separar de lo vendido esta semana.
  const f = resumen?.finanzas || {};
  const margen = f.margen || null;
  const deudasSemana = calendarioPagos(datos.deudas || []).semanal;
  const reparto = deCada100({ margen, operacion: f.operacion, deudas: f.pagosDeuda, ventasMes: meta?.meta_ventas || 0 });
  const pctTuyo = reparto && reparto.tuyo > 1 ? reparto.tuyo : 1; // Profit First: si no alcanza, empieza con 1 %: lo importante es el hábito.
  const separar = margen ? (() => {
    const mercaderia = vendido * (1 - margen);
    const tuyo = vendido * (pctTuyo / 100);
    const tarjetas = deudasSemana;
    const gastos = vendido - mercaderia - tuyo - tarjetas;
    return { mercaderia, tarjetas, tuyo, pctTuyo, gastos, noAlcanza: gastos < 0 };
  })() : null;

  // Estrategias y tareas de la semana.
  const hechas = estrategias.filter((e) => e.estado === 'hecha' && e.hecha_en && new Date(e.hecha_en) >= lunes);
  const enCurso = estrategias.filter((e) => e.estado === 'en_curso');
  const tareas = recordatorios.filter((r) => { const t = new Date(r.cuando); return t >= lunes && t <= hoy; });
  const tareasHechas = tareas.filter((r) => r.hecho).length;

  // 🏆 Lo logrado (medir el avance, no solo lo que falta).
  const logros = [];
  if (nVentas > 0) logros.push(`Cerraste ${nVentas} venta${nVentas === 1 ? '' : 's'} por ${dinero(vendido)}`);
  if (antes > 0 && vendido > antes * 1.05) logros.push(`Vendiste ${Math.round(((vendido - antes) / antes) * 100)}% más que la semana pasada`);
  if (mejorDia?.total > 0) logros.push(`Tu mejor día fue el ${mejorDia.nombre}`);
  if (top[0]) logros.push(`Lo que más se vendió: ${top.map((t) => t.nombre).join(', ')}`);
  if (hechas.length) logros.push(`Terminaste ${hechas.length} estrategia${hechas.length === 1 ? '' : 's'}: ${hechas.map((e) => e.titulo).join(' · ')}`);
  if (tareas.length && tareasHechas) logros.push(`Cumpliste ${tareasHechas} de ${tareas.length} tareas agendadas`);

  // 🔧 Qué ajustar la próxima semana.
  const ajustes = [];
  if (metaSemana > 0 && vendido < metaSemana * 0.85) ajustes.push({ tipo: 'meta', falta: metaSemana - vendido, foco: top[0]?.nombre || null });
  if (diasSinVenta >= 2) ajustes.push({ tipo: 'dias', n: diasSinVenta });
  if (tareas.length >= 3 && tareasHechas / tareas.length < 0.5) ajustes.push({ tipo: 'tareas', hechas: tareasHechas, total: tareas.length });
  (resumen?.presupuesto || []).filter((p) => p.estado === 'pasado').slice(0, 2).forEach((p) => ajustes.push({ tipo: 'gasto', categoria: p.categoria, pct: p.pct }));
  if (separar?.noAlcanza) ajustes.push({ tipo: 'noAlcanza' });
  if (!enCurso.length) ajustes.push({ tipo: 'sinEstrategia' });

  return {
    lunes: ymd(lunes), desde: lunes, hasta: hoy, vendido, antes, nVentas, ticket: nVentas ? vendido / nVentas : 0,
    porDia, mejorDia, top, metaSemana, separar, logros, ajustes, tareas: { total: tareas.length, hechas: tareasHechas }, enCurso,
  };
}
